import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { PstReference } from 'pst/models';
import { PstParserService, QuestionPart } from 'pst/services';
import { PstScreenValidAction, UserEnteredValueAction, UserSelectedChildNodeAction } from 'pst/store';
import { PstState } from 'pst/store/pst.store';
import * as fromPst from 'pst/store/pst.store';
import { combineLatest } from 'rxjs';
import { BigNumberPipe } from 'shared/pipes';
import { LoggerService, LookupDataService } from 'shared/services';
import { bigNumberMaxValidator, bigNumberMinValidator } from 'shared/validators';

import { BasePstComponent } from '../base-pst.component';

@Component({
    selector: 'wim-pst-currency',
    templateUrl: 'currency.component.pug',
    styleUrls: ['currency.component.scss'],
})
export class CurrencyComponent extends BasePstComponent implements OnInit {
    @Input()
    public customerName: string;
    public tree: PstReference;

    public heading: string;
    public description: string;

    public sliderText: QuestionPart[];
    public max = 1000000;
    public noMaxValue = false;
    public overrideMax = null;
    public min = 1;
    public step = 100;
    public inputForm: FormGroup;
    public sliderValue = 0;
    public vertical = false;
    public inputWidth: string;
    private defaultValue: string;

    constructor(
        loggerService: LoggerService,
        modalService: NgbModal,
        private pstStore: Store<PstState>,
        private pstParser: PstParserService,
        private formBuilder: FormBuilder,
        private bigNumberPipe: BigNumberPipe,
        private lookupService: LookupDataService
    ) {
        super(loggerService, modalService);
    }

    public ngOnInit() {
        const result$ = this.pstStore.select(fromPst.selectInProgressPst);
        const tree$ = this.pstStore.select(fromPst.selectPstTree);

        const tooltips$ = this.lookupService.getPstTooltips();

        combineLatest(result$, tree$, tooltips$, (result, tree, tooltips) => {
            return { result, tree, tooltips };
        })
            .pipe(this.scavenger.collect())
            .subscribe(({ result, tree, tooltips }) => {
                this.tree = tree;
                if (!this.tree) {
                    return;
                }
                super.initContent(null, tooltips, tree.node.nodeId);

                const childNode = tree.childNodes[0];
                this.pstStore.dispatch(new UserSelectedChildNodeAction(childNode));

                this.heading = this.pstParser.parseParameterizedString(tree.node.nodeHeading, result);
                this.description = tree.node.nodeDescription;
                this.sliderText = this.pstParser.buildInputDescription(childNode.nodeDescription);

                const range = this.pstParser.extractRange(childNode.nodeLongDescription);

                this.overrideMax = this.pstParser.extractMaxOverride(childNode.nodeLongDescription);

                if (range) {
                    if (isNaN(range[1])) {
                        if (this.overrideMax) {
                            this.noMaxValue = true;
                            this.min = range[0] === null ? 1 : range[0];
                        } else {
                            this.noMaxValue = false;
                        }
                    } else {
                        [this.min, this.max] = range;
                    }
                }
                this.inputWidth = this.calculatInputWidth();
                this.defaultValue = this.pstParser.extractDefaultValue(tree.node.nodeLongDescription) || '';

                this.createForm();
                this.pstStore.dispatch(new PstScreenValidAction(this.inputForm.valid));
            });
    }

    public ngOnDestroy() {}

    private calculatInputWidth() {
        return `${super.measureTextWidth(this.bigNumberPipe.transform(this.noMaxValue ? '1 000 000' : this.max))}px`;
    }

    public sliderMove(event) {
        this.inputForm.controls.value.setValue(this.bigNumberPipe.transform(event.value));
    }

    private createForm() {
        if (!this.inputForm) {
            const validators = [Validators.required, Validators.pattern('^[0-9\\s]+$'), bigNumberMinValidator(this.min)];
            if (!this.noMaxValue) {
                validators.push(bigNumberMaxValidator(this.max));
            }
            this.inputForm = this.formBuilder.group({
                value: ['', Validators.compose(validators)],
            });
            this.inputForm.valueChanges.subscribe(changes => {
                if (changes.value === null || changes.value === '') {
                    this.sliderValue = 0;
                } else {
                    this.sliderValue = +this.bigNumberPipe.parse(changes.value);
                }
                this.pstStore.dispatch(new UserEnteredValueAction(changes.value.replace(/\s/g, '')));
                this.pstStore.dispatch(new PstScreenValidAction(this.inputForm.valid));

                let newWidth = super.measureTextWidth(this.bigNumberPipe.transform(changes.value));
                if (newWidth > +this.inputWidth.replace('px', '')) {
                    this.inputWidth = `${newWidth}px`;
                }
            });
        }
        if (this.defaultValue) {
            this.inputForm.get('value').setValue(+this.defaultValue);
        }
    }
}
